import axios from 'axios';

// Use same-origin API calls for custom domains (staging.samrumah.com etc.)
// Fall back to REACT_APP_BACKEND_URL for preview environments
const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  
  // If running on a custom domain (not preview.emergentagent.com), use same-origin
  if (currentOrigin && !currentOrigin.includes('preview.emergentagent.com') && !currentOrigin.includes('localhost')) {
    return currentOrigin;
  }
  
  return envUrl || currentOrigin;
};

const BACKEND_URL = getBackendUrl();
const API_BASE = `${BACKEND_URL}/api`;

// Helper to resolve upload paths to full URLs
export const getUploadUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  // Route uploads through /api prefix for Kubernetes ingress routing
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}${path}`;
};

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // V4.17.2 — Only nuke storage + redirect if we're not already on
      // an auth screen. The previous hard `window.location.href = '/login'`
      // raced with React's render cycle right after login (the agent's
      // first fetch could 401 if it was issued via a stale interceptor),
      // wiping the freshly-saved agent token and bouncing the user back
      // to a blank /login.
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const onAuthPage = path === '/login' || path === '/register' || path === '/forgot-password';
      if (!onAuthPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  uploadKYC: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/upload-kyc', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  changePassword: (newPassword) => api.post('/auth/change-temp-password', { new_password: newPassword }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
};

// User APIs
export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  updateKYCStatus: (id, status) => api.put(`/users/${id}/kyc?status=${status}`),
  getDownline: (id) => api.get(`/users/${id}/downline`),
};

// Agent APIs
export const agentAPI = {
  getAgents: (params) => api.get('/agents', { params }),
  updateRank: (id, rank) => api.put(`/agents/${id}/rank?rank=${rank}`),
  updateEscalationContacts: (id, contacts) => api.put(`/agents/${id}/escalation-contacts`, contacts),
  getPerformance: (id) => api.get(`/agents/${id}/performance`),
  getRankProgress: (id) => api.get(`/agents/${id}/rank-progress`),
  getPerformanceMetrics: (id) => api.get(`/agents/${id}/performance-metrics`),
  getCommissionClaims: (id, params) => api.get(`/agents/${id}/commission-claims`, { params }),
};

// Rank Requirements APIs
export const rankAPI = {
  getRequirements: () => api.get('/rank-requirements'),
  updateRequirements: (data) => api.put('/rank-requirements', data),
};

// Category APIs (Master Data)
export const categoryAPI = {
  getCategories: (params) => api.get('/categories', { params }),
  createCategory: (data) => api.post('/categories', data),
  updateCategory: (id, data) => api.put(`/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
  seedLocations: () => api.post('/categories/seed-locations'),
  downloadTemplate: (type) => api.get(`/categories/template/${type}`, { responseType: 'blob' }),
  bulkImport: (type, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/categories/bulk-import/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Property APIs
export const propertyAPI = {
  getProperties: (params) => api.get('/properties', { params }),
  getProperty: (id) => api.get(`/properties/${id}`),
  createProperty: (data) => api.post('/properties', data),
  updateProperty: (id, data) => api.put(`/properties/${id}`, data),
  deleteProperty: (id) => api.delete(`/properties/${id}`),
  getAvailability: (id) => api.get(`/properties/${id}/availability`),
  uploadImages: (id, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/properties/${id}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteImage: (id, imageIndex) => api.delete(`/properties/${id}/images/${imageIndex}`),
  uploadBrochure: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/properties/${id}/upload-brochure`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteBrochure: (id) => api.delete(`/properties/${id}/brochure`),
  // Floor plan layouts (public)
  uploadLayouts: (id, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/properties/${id}/upload-layouts`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLayout: (id, layoutIndex) => api.delete(`/properties/${id}/layouts/${layoutIndex}`),
  // Sales kit (agent-only marketing material)
  uploadSalesKits: (id, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/properties/${id}/upload-sales-kits`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSalesKit: (id, kitId) => api.delete(`/properties/${id}/sales-kits/${kitId}`),
  listSalesKits: (id) => api.get(`/properties/${id}/sales-kits`),
  // Booking template linking
  getBookingTemplate: (id, unitId = null) => api.get(`/properties/${id}/booking-template`, { params: { unit_id: unitId } }),
  setBookingTemplate: (id, templateId) => api.put(`/properties/${id}/booking-template?template_id=${templateId}`),
};

// Unit APIs
export const unitAPI = {
  getUnits: (params) => api.get('/units', { params }),
  getUnit: (id) => api.get(`/units/${id}`),
  createUnit: (data) => api.post('/units', data),
  bulkCreate: (units) => api.post('/units/bulk', units),
  updateUnit: (id, data) => api.put(`/units/${id}`, data),
  updateStatus: (id, status) => api.put(`/units/${id}/status?status=${status}`),
  deleteUnit: (id) => api.delete(`/units/${id}`),
  reserveUnit: (id, agentId) => api.post(`/units/${id}/reserve`, { agent_id: agentId }),
  releaseUnit: (id) => api.post(`/units/${id}/release`),
};

// Claim APIs (only payment tracking & ledger; legacy /claims removed)
export const claimAPI = {
  // Payment tracking
  getPaymentTracker: (id) => api.get(`/commission-claims/${id}/payment-tracker`),
  markDeveloperFunded: (id, data) => api.post(`/commission-claims/${id}/mark-developer-funded`, data),
  markRecipientPaid: (id, recipientIdx, data) => api.post(`/commission-claims/${id}/recipients/${recipientIdx}/mark-paid`, data),
  downloadReceipt: (id, recipientIdx) => api.get(`/commission-claims/${id}/recipients/${recipientIdx}/receipt`),
  // Ledger
  getLedgerSummary: () => api.get('/ledger/summary'),
  getDeveloperReceivables: () => api.get('/ledger/developer-receivables'),
};

// Booking Payment APIs
export const bookingPaymentAPI = {
  recordPayment: (bookingId, data) => api.post(`/bookings/${bookingId}/payments`, data),
  uploadPaymentProof: (bookingId, formData) =>
    api.post(`/bookings/${bookingId}/payments/proof`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPayments: (bookingId) => api.get(`/bookings/${bookingId}/payments`),
  verifyPaymentRecord: (bookingId, paymentId, verified, notes = '') =>
    api.put(`/bookings/${bookingId}/payments/${paymentId}/verify?verified=${verified}&notes=${encodeURIComponent(notes)}`),
  getPendingPaymentRecords: () => api.get('/booking-payment-records/pending-verification'),
  getPaymentsSummary: (entity) => api.get('/booking-payments/summary', { params: { entity } }),
  sendReceipt: (bookingId) => api.post(`/bookings/${bookingId}/send-receipt`),
  sendPaymentReceipt: (bookingId, paymentId) => api.post(`/bookings/${bookingId}/payments/${paymentId}/send-receipt`),
};

// Wallet APIs
export const walletAPI = {
  getWallet: () => api.get('/wallet'),
  getTransactions: () => api.get('/wallet/transactions'),
};

// Withdrawal APIs
export const withdrawalAPI = {
  getWithdrawals: (params) => api.get('/withdrawals', { params }),
  createWithdrawal: (data) => api.post('/withdrawals', data),
  approve: (id) => api.put(`/withdrawals/${id}/approve`),
  complete: (id) => api.put(`/withdrawals/${id}/complete`),
  uploadSlip: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/withdrawals/${id}/payment-slip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Config APIs
export const configAPI = {
  getConfig: () => api.get('/config'),
  updateConfig: (data) => api.put('/config', data),
};

// Notification APIs
export const notificationAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Booking Template APIs
export const bookingTemplateAPI = {
  getTemplates: (params) => api.get('/booking-templates', { params }),
  getTemplate: (id) => api.get(`/booking-templates/${id}`),
  createTemplate: (data) => api.post('/booking-templates', data),
  updateTemplate: (id, data) => api.put(`/booking-templates/${id}`, data),
  uploadPdf: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/booking-templates/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteTemplate: (id) => api.delete(`/booking-templates/${id}`),
};

// Enhanced Booking APIs
export const bookingAPI = {
  getBookings: (params) => api.get('/bookings', { params }),
  getBooking: (id) => api.get(`/bookings/${id}`),
  initiateBooking: (data) => api.post('/bookings/initiate', data),
  saveForm: (id, data) => api.put(`/bookings/${id}/form`, data),
  submitBooking: (id) => api.post(`/bookings/${id}/submit`),
  uploadPaymentSlip: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/bookings/${id}/payment-slip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  verifyPayment: (id, verified, notes = '') => api.put(`/bookings/${id}/verify-payment?verified=${verified}&notes=${encodeURIComponent(notes)}`),
  getPendingVerification: () => api.get('/bookings/pending-verification'),
  getReceipt: (id) => `${API_BASE}/bookings/${id}/receipt`,
  getReceipts: () => api.get('/receipts'),
  updateLoanStatus: (id, status, bankName, loanAmount, notes = '') => 
    api.put(`/bookings/${id}/loan-application?status=${status}&bank_name=${encodeURIComponent(bankName)}&loan_amount=${loanAmount}&notes=${encodeURIComponent(notes)}`),
  updateSPA: (id, signed, spaDate = '', notes = '') =>
    api.put(`/bookings/${id}/spa?signed=${signed}&spa_date=${spaDate}&notes=${encodeURIComponent(notes)}`),
  complete: (id) => api.put(`/bookings/${id}/complete`),
  // PDF generation APIs
  generatePdf: (id) => api.get(`/bookings/${id}/generate-pdf`),
  getPdfPreview: (id) => api.get(`/bookings/${id}/pdf-preview`),
  sendPdfEmail: (id, recipientEmail, sendToCustomer = false, sendToAgent = false) => 
    api.post(`/bookings/${id}/send-pdf-email?send_to_customer=${sendToCustomer}&send_to_agent=${sendToAgent}&additional_emails=${encodeURIComponent(recipientEmail)}`),
  downloadPdf: (id) => api.get(`/bookings/${id}/pdf-preview`),
};

// Enhanced Appointment APIs
export const appointmentAPI = {
  getAppointments: (params) => api.get('/appointments', { params }),
  getAppointment: (id) => api.get(`/appointments/${id}`),
  createAppointment: (data) => api.post('/appointments', data),
  updateStatus: (id, data) => api.put(`/appointments/${id}/status`, data),
  approve: (id) => api.put(`/appointments/${id}/approve`),
  reject: (id, reason = '') => api.put(`/appointments/${id}/reject?reason=${encodeURIComponent(reason)}`),
  markViewingCompleted: (id, notes = '') => api.put(`/appointments/${id}/viewing-completed?notes=${encodeURIComponent(notes)}`),
  initiateBooking: (id) => api.put(`/appointments/${id}/initiate-booking`),
  assignHandler: (id, handlerId) => api.put(`/appointments/${id}/assign?handler_id=${handlerId}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: (entityParam = '') => api.get(`/dashboard/stats${entityParam}`),
};

// PDF Template APIs
export const pdfTemplateAPI = {
  getTemplates: (params) => api.get('/pdf-templates', { params }),
  getTemplate: (id) => api.get(`/pdf-templates/${id}`),
  createTemplate: (formData) => api.post('/pdf-templates', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateTemplate: (id, data) => api.put(`/pdf-templates/${id}`, data),
  updateFormFields: (id, fields) => api.put(`/pdf-templates/${id}/form-fields`, fields),
  updateFieldMappings: (id, mappings) => api.put(`/pdf-templates/${id}/field-mappings`, mappings),
  updateSignaturePositions: (id, positions) => api.put(`/pdf-templates/${id}/signature-positions`, positions),
  deleteTemplate: (id) => api.delete(`/pdf-templates/${id}`),
  previewTemplate: (id) => `${API_BASE}/pdf-templates/${id}/preview`,
  testFill: (id, data) => api.post(`/pdf-templates/${id}/test-fill`, data),
  getGeneratedPdf: (filename) => `${API_BASE}/generated-pdfs/${filename}`,
};

// Property Template Mapping
export const propertyTemplateAPI = {
  setTemplate: (propertyId, templateId) => api.put(`/properties/${propertyId}/template?template_id=${templateId}`),
  getTemplate: (propertyId) => api.get(`/properties/${propertyId}/template`),
};

// Developer Template APIs
export const developerTemplateAPI = {
  getTemplates: (params) => api.get('/developer-templates', { params }),
  getTemplate: (id) => api.get(`/developer-templates/${id}`),
  uploadTemplate: (formData) => api.post('/developer-templates/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateTemplate: (id, data) => api.put(`/developer-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/developer-templates/${id}`),
  getPreview: (id, page = 0) => api.get(`/developer-templates/${id}/preview?page=${page}`),
  updateFields: (id, fields) => api.put(`/developer-templates/${id}/fields`, { fields }),
  activateTemplate: (id) => api.put(`/developer-templates/${id}/activate`),
  reDetect: (id) => api.post(`/developer-templates/${id}/re-detect`),
  bulkImport: (sourceDir) => api.post('/developer-templates/bulk-import', { source_dir: sourceDir }),
  bulkDetect: () => api.post('/developer-templates/bulk-detect'),
  getFieldsForBooking: (id) => api.get(`/developer-templates/${id}/fields-for-booking`),
  detectPositions: (id) => api.post(`/developer-templates/${id}/detect-positions`),
  previewOverlay: (id, data) => api.post(`/developer-templates/${id}/preview-overlay`, data),
  generateFilled: (id, data) => api.post(`/developer-templates/${id}/generate-filled`, data),
  testFillPreview: (id, page = 0, dpi = 130) => api.get(`/developer-templates/${id}/test-fill-preview`, { params: { page, dpi } }),
  overrideFieldPosition: (id, fieldId, payload) => api.patch(`/developer-templates/${id}/fields/${fieldId}/position`, payload),
  backfillPositions: () => api.post('/developer-templates/backfill-positions'),
  bulkReanchorPoor: (onlyPoor = true) => api.post(`/developer-templates/bulk-reanchor-poor?only_poor=${onlyPoor}`),
  healTemplate: (id, aggressive = true) => api.post(`/developer-templates/${id}/heal?aggressive=${aggressive}`),
  verifyTemplate: (id, verified = true) => api.post(`/developer-templates/${id}/verify`, { verified }),
};

// Booking with Form APIs
export const bookingFormAPI = {
  createWithForm: (data) => api.post('/bookings/with-form', data),
};

// Open Leads APIs
export const leadsAPI = {
  getOpenLeads: () => api.get('/leads/open'),
  acceptLead: (id, data = {}) => api.post(`/leads/${id}/accept`, data),
  getMyAccepted: () => api.get('/leads/my-accepted'),
  checkAccess: () => api.get('/leads/check-access'),
  // Admin
  getDashboard: () => api.get('/admin/leads/dashboard'),
  assignLead: (id, data) => api.post(`/admin/leads/${id}/assign`, data),
  getConfig: () => api.get('/admin/leads/config'),
  updateConfig: (data) => api.put('/admin/leads/config', data),
  runExpiry: () => api.post('/admin/leads/run-expiry'),
  reopenLead: (id) => api.post(`/admin/leads/${id}/reopen`),
  setTargetRanks: (id, data) => api.put(`/admin/leads/${id}/target-ranks`, data),
};

// Survey APIs
export const surveyAPI = {
  getPending: () => api.get('/surveys/pending'),
  submit: (id, data) => api.post(`/surveys/${id}/submit`, data),
  skip: (id) => api.post(`/surveys/${id}/skip`),
  getMeasures: () => api.get('/surveys/measures'),
  // Admin
  getAll: (params = '') => api.get(`/admin/surveys${params}`),
  getFlagged: () => api.get('/admin/surveys/flagged'),
  resolve: (id, data) => api.put(`/admin/surveys/${id}/resolve`, data),
  getAgentPerformance: () => api.get('/admin/agent-performance'),
  updateConfig: (data) => api.put('/admin/surveys/config', data),
};

// Perks & Benefits APIs
export const perksAPI = {
  getCategories: () => api.get('/perks/categories'),
  getTypes: () => api.get('/perks/types'),
  browse: (params) => api.get('/perks/browse', { params }),
  getFeatured: (limit = 6) => api.get('/perks/featured', { params: { limit } }),
  getDetail: (id) => api.get(`/perks/${id}`),
  usePerk: (id) => api.post(`/perks/${id}/use`),
  getMyHistory: (params) => api.get('/perks/my/history', { params }),
  // Admin
  adminList: (params) => api.get('/perks/admin/list', { params }),
  adminCreate: (data) => api.post('/perks/admin/create', data),
  adminUpdate: (id, data) => api.put(`/perks/admin/${id}`, data),
  adminDelete: (id) => api.delete(`/perks/admin/${id}`),
  adminUploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/perks/admin/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  adminGetRedemptions: (params) => api.get('/perks/admin/redemptions', { params }),
  adminGetStats: () => api.get('/perks/admin/stats'),
};

// Banners APIs (CMS-managed marketing banners shown on the mobile homepage)
export const bannersAPI = {
  adminList: (params) => api.get('/banners/admin/list', { params }),
  adminGet: (id) => api.get(`/banners/admin/${id}`),
  adminCreate: (data) => api.post('/banners/admin/create', data),
  adminUpdate: (id, data) => api.put(`/banners/admin/${id}`, data),
  adminDelete: (id) => api.delete(`/banners/admin/${id}`),
  adminUploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/banners/admin/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Analytics APIs
export const analyticsAPI = {
  // 10 Reports
  monthlyPerformance: (params) => api.get('/analytics/report/monthly-performance', { params }),
  agentLeaderboard: (params) => api.get('/analytics/report/agent-leaderboard', { params }),
  leaderRankings: (params) => api.get('/analytics/report/leader-rankings', { params }),
  bookingByProject: (params) => api.get('/analytics/report/booking-by-project', { params }),
  commissionPipeline: (params) => api.get('/analytics/report/commission-pipeline', { params }),
  entityComparison: (params) => api.get('/analytics/report/entity-comparison', { params }),
  conversionFunnel: (params) => api.get('/analytics/report/conversion-funnel', { params }),
  projectHeatmap: (params) => api.get('/analytics/report/project-heatmap', { params }),
  fastCommAnalysis: (params) => api.get('/analytics/report/fast-comm-analysis', { params }),
  executiveKpi: (params) => api.get('/analytics/report/executive-kpi', { params }),
  // Export
  exportReport: (report, format, params) => api.get(`/analytics/export/${report}`, { params: { format, ...params }, responseType: format === 'csv' ? 'blob' : 'blob' }),
  // Legacy
  getDashboardSummary: (params) => api.get('/analytics/dashboard-summary', { params }),
  getSalesByYear: () => api.get('/analytics/sales-by-year'),
  getTopPerformers: (params) => api.get('/analytics/top-performers', { params }),
  getAgentHierarchy: () => api.get('/analytics/agent-hierarchy'),
  getCommissionWorkflow: () => api.get('/analytics/commission-workflow'),
  getMonthlySales: (params) => api.get('/analytics/monthly-sales', { params }),
  getCommissionOverview: () => api.get('/commission-analytics/overview'),
  getRankPerformance: () => api.get('/commission-analytics/rank-performance'),
};

// Chat APIs
export const chatAPI = {
  createSession: (data = {}) => api.post('/chat/sessions', data),
  listSessions: () => api.get('/chat/sessions'),
  getHistory: (sessionId) => api.get(`/chat/sessions/${sessionId}/history`),
  sendMessage: (sessionId, message) => api.post(`/chat/sessions/${sessionId}/message`, { message }),
  deleteSession: (sessionId) => api.delete(`/chat/sessions/${sessionId}`),
  // Public config (quick actions + capability flags) — consumed by ChatBubble.
  getPublicConfig: () => api.get('/public/v1/chat/config'),
};

// AI Concierge — Admin Settings (V4.16.21)
export const chatSettingsAPI = {
  get: () => api.get('/admin/chat/settings'),
  update: (payload) => api.put('/admin/chat/settings', payload),
  reset: () => api.post('/admin/chat/settings/reset'),
  test: (message, roleOverride = 'customer') =>
    api.post('/admin/chat/test', { message, role_override: roleOverride }),
};

export default api;
